import { ToggleButtonGroupProps } from '@mui/material/ToggleButtonGroup';
import {
  PaintBlockSelectionPill,
  PaintBlockSelectionPillCounter,
  StyledToggleButtonGroup,
} from './styles';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import * as localState from '../../state';
import { usePaintRegionGroupsQuery } from '@/__generated__/graphql';
import { useMemo } from 'react';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { Skeleton } from '@mui/material';
import { useFeatureFlag } from '@/hooks';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks';

type Props = {
  compact?: boolean;
  onSavedBlocksClick?: () => void;
  defaultPaintGroupCount?: number;
} & ToggleButtonGroupProps;

const SAVED_BLOCKS_VALUE = 'saved-blocks';

export const PaintBlockTopFilter = ({
  sx,
  compact = false,
  defaultPaintGroupCount = 0,
  ...toggleGroupProps
}: Props) => {
  const structureId = useGetStructureId();
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const [selectedPaintRegionGroupId, setelectedPaintRegionGroupId] = useAtom(
    state.selectedPaintRegionGroupId
  );
  const setCustomBlocksQueryInput = useSetAtom(localState.customBlocksQueryInput);
  const setAllPaintRegionGroups = useSetAtom(state.allPaintRegionGroups);
  const selectedPaintRegionGroup = useAtomValue(state.selectedPaintRegionGroup);

  const [isSavedBlocksSelected, setIsSavedBlocksSelected] = useAtom(state.isSavedBlocksSelected);
  const isAlphaUser = useFeatureFlag('alpha-user');

  const { data, loading } = usePaintRegionGroupsQuery({
    variables: {
      input: { structureId, templateId: selectedAssemblyTemplateId || '' },
    },
    skip: !selectedAssemblyTemplateId || !structureId,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    onCompleted: (response) => {
      // Initializing the filter to the default filter
      // which is 'Paint Regions'
      const defaultPaintGroupId = response?.paintRegionGroups?.find((item) => item.isDefault)?.id;
      setelectedPaintRegionGroupId((prev) => prev || defaultPaintGroupId);
      setAllPaintRegionGroups(response?.paintRegionGroups || []);
    },
  });

  const options = useMemo(() => {
    const paintRegionOptions = (data?.paintRegionGroups || []).map((group) => ({
      value: group?.id,
      label: group?.name,
      count: 0,
      type: 'paint-region' as const,
    }));

    // Only add Saved Blocks option if there are paint region options
    if (paintRegionOptions.length > 0) {
      const savedBlocksOption = {
        value: SAVED_BLOCKS_VALUE,
        label: compact ? 'Saved' : 'Saved Blocks',
        count: 0,
        type: 'saved-blocks' as const,
      };
      return [...paintRegionOptions, savedBlocksOption];
    }

    return paintRegionOptions;
  }, [data?.paintRegionGroups, compact]);

  if (!isAlphaUser) return <></>;

  if (loading)
    return (
      <Skeleton variant="rectangular" width={'70%'} height={30} sx={{ borderRadius: 2, mt: 1 }} />
    );

  if (!options?.length) return <></>;

  // Determine the current selected value
  const currentValue = isSavedBlocksSelected
    ? SAVED_BLOCKS_VALUE
    : selectedPaintRegionGroupId || SAVED_BLOCKS_VALUE;

  return (
    <StyledToggleButtonGroup
      value={currentValue}
      exclusive
      sx={sx}
      onChange={(_, value: string) => {
        if (!value) return;

        // Handle Saved Blocks selection
        if (value === SAVED_BLOCKS_VALUE) {
          setelectedPaintRegionGroupId(undefined);
          setIsSavedBlocksSelected(true);
          return;
        }

        // Handle paint region group selection
        setIsSavedBlocksSelected(false);
        if (value === selectedPaintRegionGroupId) return;

        const foundGroup = data?.paintRegionGroups?.find((group) => group.id === value);
        setelectedPaintRegionGroupId(foundGroup?.id);
        // Resetting this state, to let the CustomBlockList know that
        // there are more items to fetch
        setCustomBlocksQueryInput((prev) => ({
          ...prev,
          hasMoreItems: true,
          page: 2,
        }));
      }}
      {...toggleGroupProps}
    >
      {options.map(({ label, value }) => (
        <PaintBlockSelectionPill key={value} value={value}>
          {compact ? label.replace('Blocks', '') : label}
          {!compact &&
            selectedPaintRegionGroup?.isDefault &&
            selectedPaintRegionGroupId === value && (
              <PaintBlockSelectionPillCounter>
                {defaultPaintGroupCount}
              </PaintBlockSelectionPillCounter>
            )}
        </PaintBlockSelectionPill>
      ))}
    </StyledToggleButtonGroup>
  );
};
